import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { orgName, brand, logoMark } from '../brand';

/**
 * Support plan PDF exporter.
 *
 * Aesthetic goals (per stakeholder feedback — these documents are sometimes
 * shared with government bodies, so they need to read as serious, orderly,
 * and professional):
 *  - Restrained typographic hierarchy: ONE title per page, no double-headers.
 *  - COR purple as the only accent; everything else in neutral slate.
 *  - Generous whitespace + thin rules instead of heavy colored bars.
 *  - Each top-level section starts on a fresh page (preserves the existing
 *    no-mid-section-breaks behaviour the team already approved).
 *  - Subsections use minPresenceAhead on their title so a heading never
 *    orphans at the bottom of a page, but long subsection bodies are
 *    allowed to break naturally across pages (otherwise wrap={false}
 *    forces overflow when content exceeds one page's worth of space).
 */

const DEFAULT_LOGO_PATH = '/cor_logo.png';
let brandLogoPath = DEFAULT_LOGO_PATH;

// Brand palette
const PURPLE = '#863698';
const PURPLE_DEEP = '#5e2470';
const SLATE_900 = '#0f172a';
const SLATE_700 = '#334155';
const SLATE_500 = '#64748b';
const SLATE_300 = '#cbd5e1';
const SLATE_100 = '#f1f5f9';
const SLATE_50 = '#f8fafc';

const styles = StyleSheet.create({
  // ---------- Page chrome ----------
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: 56,
    paddingBottom: 56,
    paddingHorizontal: 56,
    fontFamily: 'Helvetica',
    color: SLATE_900,
    fontSize: 10.5,
    lineHeight: 1.5,
  },

  // Top running header (appears on content pages, not the cover)
  runningHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 9,
    color: SLATE_500,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  runningRule: {
    borderBottomWidth: 0.6,
    borderBottomColor: SLATE_300,
    marginBottom: 20,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 32,
    left: 56,
    right: 56,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
    borderTopWidth: 0.6,
    borderTopColor: SLATE_300,
    fontSize: 8.5,
    color: SLATE_500,
  },

  // ---------- Cover page ----------
  cover: {
    flex: 1,
    justifyContent: 'flex-start',
    paddingTop: 80,
  },
  coverLogo: {
    width: 110,
    height: 110,
    objectFit: 'contain',
    marginBottom: 36,
  },
  coverEyebrow: {
    fontSize: 10,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
    color: PURPLE,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  coverTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: SLATE_900,
    letterSpacing: -0.4,
    marginBottom: 6,
    lineHeight: 1.15,
  },
  coverSubject: {
    fontSize: 20,
    color: SLATE_700,
    fontWeight: 'normal',
    marginBottom: 28,
    letterSpacing: -0.1,
  },
  coverRule: {
    borderBottomWidth: 1.2,
    borderBottomColor: PURPLE,
    width: 64,
    marginBottom: 24,
  },
  coverMetaRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  coverMetaLabel: {
    fontSize: 9,
    letterSpacing: 1.3,
    textTransform: 'uppercase',
    color: SLATE_500,
    width: 130,
  },
  coverMetaValue: {
    fontSize: 11,
    color: SLATE_900,
    flex: 1,
  },
  coverConfidential: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    fontSize: 9,
    color: SLATE_500,
    textAlign: 'center',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },

  // ---------- Section headers ----------
  // Top-of-page section title. Single line, big, with a self-sizing
  // accent underline that hugs the text (looks consistent regardless of
  // how long the title is, instead of a fixed-width orphan rule).
  sectionTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: SLATE_900,
    marginBottom: 22,
    paddingBottom: 4,
    letterSpacing: -0.2,
    textDecoration: 'underline',
    textDecorationColor: PURPLE,
  },
  sectionEyebrow: {
    fontSize: 9,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    color: PURPLE,
    fontWeight: 'bold',
    marginBottom: 4,
  },

  // Subsection within a section page
  subsection: {
    marginBottom: 18,
  },
  subsectionTitle: {
    fontSize: 12.5,
    fontWeight: 'bold',
    color: SLATE_900,
    marginBottom: 6,
    letterSpacing: -0.05,
  },

  // ---------- Body ----------
  paragraph: {
    fontSize: 10.5,
    lineHeight: 1.55,
    color: SLATE_700,
    marginBottom: 6,
  },
  listItem: {
    flexDirection: 'row',
    fontSize: 10.5,
    lineHeight: 1.55,
    color: SLATE_700,
    marginBottom: 3,
  },
  listBullet: {
    width: 14,
    color: PURPLE,
  },

  // ---------- Two-column meta blocks (contact info) ----------
  twoCol: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 22,
  },
  col: {
    flex: 1,
  },
  groupLabel: {
    fontSize: 9,
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    color: PURPLE,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  field: {
    flexDirection: 'row',
    marginBottom: 4,
    fontSize: 10,
  },
  fieldLabel: {
    width: 95,
    color: SLATE_500,
  },
  fieldValue: {
    flex: 1,
    color: SLATE_900,
  },

  // ---------- Table (medical practitioners) ----------
  table: {
    marginTop: 6,
    marginBottom: 18,
    borderTopWidth: 0.6,
    borderTopColor: SLATE_300,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.6,
    borderBottomColor: SLATE_300,
  },
  tableHeaderRow: {
    backgroundColor: SLATE_50,
  },
  tableCell: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontSize: 9.5,
    color: SLATE_700,
  },
  tableHeaderCell: {
    fontSize: 8.5,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    color: SLATE_500,
    fontWeight: 'bold',
  },

  // ---------- Emergency contacts list ----------
  emergencyItem: {
    flexDirection: 'row',
    marginBottom: 4,
    fontSize: 10,
    color: SLATE_700,
  },
  emergencyIndex: {
    width: 18,
    color: SLATE_500,
  },

  // ---------- Vision / hearing / seizures checkbox blocks ----------
  checkboxRow: {
    flexDirection: 'row',
    marginBottom: 6,
    gap: 18,
    flexWrap: 'wrap',
  },
  checkboxItem: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 10,
    color: SLATE_700,
  },
  checkboxBox: {
    width: 10,
    height: 10,
    borderWidth: 0.8,
    borderColor: SLATE_700,
    marginRight: 6,
    textAlign: 'center',
    fontSize: 8,
    lineHeight: 1,
  },

  // ---------- Crisis cycle phase cards ----------
  crisisPhase: {
    borderLeftWidth: 2,
    borderLeftColor: PURPLE,
    paddingLeft: 14,
    marginBottom: 16,
  },
  crisisPhaseTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: SLATE_900,
    marginBottom: 2,
  },
  crisisPhaseHint: {
    fontSize: 9,
    fontStyle: 'italic',
    color: SLATE_500,
    marginBottom: 6,
  },
  crisisPhaseBody: {
    fontSize: 10.5,
    color: SLATE_700,
    lineHeight: 1.5,
    marginBottom: 6,
  },
  crisisSupport: {
    marginTop: 4,
    paddingTop: 6,
    borderTopWidth: 0.5,
    borderTopColor: SLATE_300,
  },
  crisisSupportLabel: {
    fontSize: 8.5,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: PURPLE,
    fontWeight: 'bold',
    marginBottom: 3,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const markdownToText = (markdown) => {
  if (!markdown) return '';
  return markdown
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/^-\s+/gm, '• ')
    .replace(/\n\n+/g, '\n\n')
    .trim();
};

const renderParagraphs = (text) => {
  if (!text) return null;
  const blocks = text.split(/\n+/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    if (block.startsWith('• ')) {
      return (
        <View key={i} style={styles.listItem}>
          <Text style={styles.listBullet}>•</Text>
          <Text style={{ flex: 1 }}>{block.slice(2)}</Text>
        </View>
      );
    }
    return (
      <Text key={i} style={styles.paragraph}>
        {block}
      </Text>
    );
  });
};

const PageFooter = ({ pageNumber, total, individualName }) => (
  <View style={styles.footer} fixed>
    <Text>{individualName} — Comprehensive Support Plan</Text>
    <Text>Page {pageNumber} of {total}</Text>
  </View>
);

const RunningHeader = ({ individualName, section }) => (
  <>
    <View style={styles.runningHeader} fixed>
      <Text>{individualName} — Support Plan</Text>
      <Text>{section}</Text>
    </View>
    <View style={styles.runningRule} fixed />
  </>
);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MedicalContactSection = ({ data }) => {
  if (!data) return null;
  return (
    <View>
      <View style={styles.twoCol}>
        <View style={styles.col}>
          <Text style={styles.groupLabel}>Basic Information</Text>
          <Field label="Name" value={data.basicInfo?.name} />
          <Field label="Address" value={data.basicInfo?.address} />
          <Field label="Telephone" value={data.basicInfo?.telephone} />
          <Field label="Birth date" value={data.basicInfo?.birthDate} />
        </View>
        <View style={styles.col}>
          <Text style={styles.groupLabel}>Support Information</Text>
          <Field label="Home supports start" value={data.supportDates?.homeSupportsStart} />
          <Field label="Vocational provider" value={data.supportDates?.vocationalProvider} />
        </View>
      </View>

      {(() => {
        // Emergency contacts arrive as [{name, role, phone}, ...] — filter out
        // the empty rows the template ships with, then render formatted lines.
        const realContacts = (data.emergencyContacts || []).filter(
          (c) => c && (c.name || c.role || c.phone),
        );
        if (!realContacts.length) return null;
        return (
          <View wrap={false}>
            <Text style={styles.groupLabel}>Emergency Contacts</Text>
            {realContacts.map((c, i) => {
              const tail = [c.role, c.phone].filter(Boolean).join(' · ');
              const line = tail ? `${c.name || 'Unnamed'} — ${tail}` : (c.name || '—');
              return (
                <View key={i} style={styles.emergencyItem}>
                  <Text style={styles.emergencyIndex}>{i + 1}.</Text>
                  <Text style={{ flex: 1 }}>{line}</Text>
                </View>
              );
            })}
          </View>
        );
      })()}

      <View wrap={false} style={{ marginTop: 16 }}>
        <Text style={styles.groupLabel}>Medical Practitioners</Text>
        <View style={styles.table}>
          <PractitionerRow
            left={{ label: 'General Practitioner', value: data.medicalPractitioners?.generalPractitioner }}
            right={{ label: 'Optometrist', value: data.medicalPractitioners?.optometrist }}
          />
          <PractitionerRow
            left={{ label: 'Dentist', value: data.medicalPractitioners?.dentist }}
            right={{ label: 'Psychiatrist', value: data.medicalPractitioners?.psychiatrist }}
          />
          <PractitionerRow
            left={{ label: 'Pharmacy', value: data.medicalPractitioners?.pharmacy }}
            right={{ label: 'Other', value: typeof data.medicalPractitioners?.other === 'string'
              ? { name: data.medicalPractitioners.other }
              : data.medicalPractitioners?.other }}
          />
        </View>
      </View>
    </View>
  );
};

const Field = ({ label, value }) => (
  <View style={styles.field}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <Text style={styles.fieldValue}>{value || '—'}</Text>
  </View>
);

/**
 * Render one row of the practitioners table (two practitioners side-by-side).
 * Both `left` and `right` are objects { label, value: {name, address, city?, telephone} | undefined }.
 * Empty cells show "—".
 */
const PractitionerRow = ({ left, right }) => {
  const renderCell = (cell) => {
    const v = cell.value || {};
    const lines = [
      v.name,
      v.address || (v.city ? '' : ''),
      v.city ? (v.address ? `, ${v.city}` : v.city) : '',
      v.telephone,
    ].filter(Boolean);
    return (
      <View style={styles.tableCell}>
        <Text style={[styles.tableHeaderCell, { marginBottom: 4 }]}>{cell.label}</Text>
        {lines.length ? (
          lines.map((line, i) => (
            <Text key={i} style={{ fontSize: 9.5, color: SLATE_700 }}>{line}</Text>
          ))
        ) : (
          <Text style={{ fontSize: 9.5, color: SLATE_500 }}>—</Text>
        )}
      </View>
    );
  };
  return (
    <View style={styles.tableRow}>
      {renderCell(left)}
      {renderCell(right)}
    </View>
  );
};

const VisionHearingSection = ({ data }) => {
  if (!data) return null;
  return (
    <View wrap={false} style={styles.subsection}>
      <Text style={styles.subsectionTitle}>Vision, Hearing & Seizures</Text>

      <Checkboxes
        label="Vision"
        options={['Wears glasses', 'Wears contacts', 'Low vision', 'Blind']}
        selected={data.vision || {}}
        notes={data.visionNotes}
      />
      <Checkboxes
        label="Hearing"
        options={['Wears hearing aid', 'Hard of hearing', 'Deaf']}
        selected={data.hearing || {}}
        notes={data.hearingNotes}
      />
      <Checkboxes
        label="Seizures"
        options={['History of seizures', 'Currently has seizures', 'Takes seizure medication']}
        selected={data.seizures || {}}
        notes={data.seizureNotes}
      />
    </View>
  );
};

const Checkboxes = ({ label, options, selected, notes }) => (
  <View style={{ marginBottom: 12 }}>
    <Text style={[styles.groupLabel, { marginBottom: 4 }]}>{label}</Text>
    <View style={styles.checkboxRow}>
      {options.map((opt) => (
        <View key={opt} style={styles.checkboxItem}>
          <Text style={styles.checkboxBox}>{selected[opt] ? '✓' : ' '}</Text>
          <Text>{opt}</Text>
        </View>
      ))}
    </View>
    {notes ? <Text style={styles.paragraph}>{notes}</Text> : null}
  </View>
);

const CrisisCycleSection = ({ data }) => {
  if (!data) return null;
  const phases = [
    { id: 'Baseline', hint: 'How the individual presents at their personal best' },
    { id: 'Escalation', hint: 'Early warning signs and triggers' },
    { id: 'Crisis', hint: 'Peak intensity behaviours' },
    { id: 'Recovery', hint: 'Coming down after the peak' },
    { id: 'PostCrisis', hint: 'Returning to baseline' },
  ];
  return (
    <View>
      <Text style={styles.subsectionTitle}>Individualized Crisis Cycle</Text>
      {phases.map((p) => {
        const body = data[`phase${p.id}`];
        const support = data[`supportResponse${p.id}`];
        if (!body && !support) return null;
        return (
          <View key={p.id} wrap={false} style={styles.crisisPhase}>
            <Text style={styles.crisisPhaseTitle}>{p.id.replace(/([A-Z])/g, ' $1').trim()}</Text>
            <Text style={styles.crisisPhaseHint}>{p.hint}</Text>
            {body ? <Text style={styles.crisisPhaseBody}>{body}</Text> : null}
            {support ? (
              <View style={styles.crisisSupport}>
                <Text style={styles.crisisSupportLabel}>Support Response</Text>
                <Text style={styles.crisisPhaseBody}>{support}</Text>
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Section catalogue
// ---------------------------------------------------------------------------

const SUPPORT_PLAN_SECTIONS = [
  {
    id: 'one-page-profile',
    title: 'One Page Profile',
    subsections: [{ id: 'about-myself', title: 'Let Me Tell You About Myself' }],
  },
  { id: 'personal-biography', title: 'Personal Biography' },
  {
    id: 'quality-of-life',
    title: 'Quality of Life Domains',
    subsections: [
      { id: 'physical-wellbeing', title: 'Physical Wellbeing' },
      { id: 'emotional-wellbeing', title: 'Emotional Wellbeing' },
      { id: 'self-determination', title: 'Self Determination' },
      { id: 'personal-development', title: 'Personal Development' },
      { id: 'interpersonal-relations', title: 'Interpersonal Relations' },
      { id: 'social-inclusion', title: 'Social Inclusion' },
      { id: 'material-wellbeing', title: 'Material Wellbeing' },
      { id: 'rights', title: 'Rights' },
      { id: 'personal-environment', title: 'Personal Environment' },
      { id: 'personal-supports', title: 'Personal Supports' },
    ],
  },
  {
    id: 'health-wellness',
    title: 'Health & Wellness',
    subsections: [
      { id: 'general-health', title: 'General Health' },
      { id: 'surgical-therapeutic', title: 'Surgical / Therapeutic' },
      { id: 'medical-equipment', title: 'Medical Equipment Requirements' },
      { id: 'medical-appointments', title: 'Medical Appointments' },
      { id: 'mental-health', title: 'Mental Health' },
      { id: 'diet', title: 'Diet' },
      { id: 'mobility', title: 'Mobility' },
      { id: 'medication-needs', title: 'Medication Needs' },
    ],
  },
  {
    id: 'emotional-development',
    title: 'Personal & Emotional Development',
    subsections: [
      { id: 'profile', title: 'Profile' },
      { id: 'emotional-needs', title: 'Emotional Needs' },
      { id: 'spiritual-needs', title: 'Spiritual Needs or Preferences' },
      { id: 'dating-relationships', title: 'Dating & Personal Relationships' },
      { id: 'independence', title: 'Independence' },
    ],
  },
  {
    id: 'personal-care',
    title: 'Personal Care',
    subsections: [
      { id: 'personal-hygiene', title: 'Personal Hygiene' },
      { id: 'bathing', title: 'Bathing' },
      { id: 'skin-care', title: 'Skin Care' },
      { id: 'dental', title: 'Dental' },
      { id: 'dressing', title: 'Dressing' },
      { id: 'toileting', title: 'Toileting' },
      { id: 'sleeping', title: 'Sleeping' },
      { id: 'eating', title: 'Eating' },
      { id: 'mobility-care', title: 'Mobility' },
    ],
  },
  {
    id: 'communication',
    title: 'Communication & Learning',
    subsections: [
      { id: 'receptive', title: 'Receptive' },
      { id: 'expressive', title: 'Expressive' },
      { id: 'learning-preferences', title: 'Learning Preferences' },
      { id: 'assistive-technology', title: 'Assistive Technology' },
    ],
  },
  {
    id: 'community-inclusion',
    title: 'Community Inclusion & Lifestyle',
    subsections: [
      { id: 'household-activities', title: 'Household Activities' },
      { id: 'leisure-recreation', title: 'Leisure & Recreation' },
    ],
  },
  {
    id: 'employment-volunteer',
    title: 'Employment, Volunteer & Day Program',
    subsections: [
      { id: 'present-employment', title: 'Present Employment / Day Program' },
      { id: 'former-employment', title: 'Former Employment' },
      { id: 'goals-future-employment', title: 'Goals for Future Employment' },
      { id: 'employment-needs', title: 'Employment Needs' },
    ],
  },
  {
    id: 'continuing-education',
    title: 'Continuing Education',
    subsections: [
      { id: 'current-education', title: 'Current Education' },
      { id: 'educational-courses', title: 'Educational Courses Participated In' },
      { id: 'educational-interests', title: 'Educational Interests' },
      { id: 'educational-needs', title: 'Educational Needs' },
      { id: 'transportation', title: 'Transportation' },
      { id: 'money-budgeting', title: 'Money & Budgeting' },
    ],
  },
  {
    id: 'crisis-prevention',
    title: 'Crisis Prevention & Intervention',
    subsections: [
      { id: 'environmental-ecological', title: 'Environmental / Ecological Strategies' },
      { id: 'focused-support', title: 'Focused Support' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Main document
// ---------------------------------------------------------------------------

const CarePlanPDF = ({ patient, sectionContents, medicalContactData, visionHearingData, crisisCycleData }) => {
  const individualName = patient?.name || 'Individual';
  // Cover + Contact + one page per section + crisis cycle if present
  const totalPages = 2 + SUPPORT_PLAN_SECTIONS.length + (crisisCycleData ? 1 : 0);

  let pageNo = 0;
  const nextPage = () => ++pageNo;

  // Pick out content for a section/subsection from the bag passed in
  const contentFor = (sectionId, subsectionId) => {
    const key = subsectionId ? `${sectionId}-${subsectionId}` : sectionId;
    return sectionContents?.[key];
  };

  return (
    <Document>
      {/* ============== Cover ============== */}
      <Page size="A4" style={styles.page}>
        <View style={styles.cover}>
          <Image style={styles.coverLogo} src={brandLogoPath} />
          <Text style={styles.coverEyebrow}>{orgName()}</Text>
          <Text style={styles.coverTitle}>Comprehensive Support Plan</Text>
          <Text style={styles.coverSubject}>{individualName}</Text>
          <View style={styles.coverRule} />

          <View style={styles.coverMetaRow}>
            <Text style={styles.coverMetaLabel}>Generated</Text>
            <Text style={styles.coverMetaValue}>{new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</Text>
          </View>
          {patient?.age != null && (
            <View style={styles.coverMetaRow}>
              <Text style={styles.coverMetaLabel}>Age</Text>
              <Text style={styles.coverMetaValue}>{patient.age}</Text>
            </View>
          )}
          {patient?.patient_id && (
            <View style={styles.coverMetaRow}>
              <Text style={styles.coverMetaLabel}>Individual ID</Text>
              <Text style={styles.coverMetaValue}>{patient.patient_id}</Text>
            </View>
          )}
          {patient?.care_agent && (
            <View style={styles.coverMetaRow}>
              <Text style={styles.coverMetaLabel}>Primary support</Text>
              <Text style={styles.coverMetaValue}>{patient.care_agent}</Text>
            </View>
          )}
        </View>
        <Text style={styles.coverConfidential}>
          Confidential — for authorized use only
        </Text>
      </Page>

      {/* ============== Contact Information ============== */}
      <Page size="A4" style={styles.page}>
        <RunningHeader individualName={individualName} section="Contact" />
        <Text style={styles.sectionEyebrow}>Section 01</Text>
        <Text style={styles.sectionTitle}>Personal & Medical Contact Information</Text>
        <MedicalContactSection data={medicalContactData} />
        <PageFooter pageNumber={2} total={totalPages} individualName={individualName} />
      </Page>

      {/* ============== Content sections (one per top-level entry) ============== */}
      {SUPPORT_PLAN_SECTIONS.map((section, idx) => {
        const sectionNo = String(idx + 2).padStart(2, '0');
        const pageNumber = idx + 3;
        return (
          <Page key={section.id} size="A4" style={styles.page}>
            <RunningHeader individualName={individualName} section={section.title} />
            <Text style={styles.sectionEyebrow}>Section {sectionNo}</Text>
            <Text style={styles.sectionTitle}>{section.title}</Text>
    
            {/* Health & Wellness page also embeds the vision/hearing/seizure block */}
            {section.id === 'health-wellness' && visionHearingData && (
              <VisionHearingSection data={visionHearingData} />
            )}

            {section.subsections ? (
              section.subsections.map((sub) => {
                const text = markdownToText(contentFor(section.id, sub.id));
                if (!text) return null;
                return (
                  <View key={sub.id} style={styles.subsection}>
                    {/* Keep the title with at least the first few lines of
                        body so it never orphans at page bottom, but allow
                        long bodies to break naturally across pages. */}
                    <View wrap={false} minPresenceAhead={60}>
                      <Text style={styles.subsectionTitle}>{sub.title}</Text>
                    </View>
                    {renderParagraphs(text)}
                  </View>
                );
              })
            ) : (
              renderParagraphs(markdownToText(contentFor(section.id)))
            )}

            <PageFooter pageNumber={pageNumber} total={totalPages} individualName={individualName} />
          </Page>
        );
      })}

      {/* ============== Crisis cycle (own page if data present) ============== */}
      {crisisCycleData && (
        <Page size="A4" style={styles.page}>
          <RunningHeader individualName={individualName} section="Crisis Cycle" />
          <Text style={styles.sectionEyebrow}>Section {String(SUPPORT_PLAN_SECTIONS.length + 2).padStart(2, '0')}</Text>
          <Text style={styles.sectionTitle}>Individualized Crisis Cycle</Text>
            <CrisisCycleSection data={crisisCycleData} />
          <PageFooter pageNumber={totalPages} total={totalPages} individualName={individualName} />
        </Page>
      )}
    </Document>
  );
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------


// The PDF stylesheet is built at import time, before the brand has loaded, so
// the brand's colours + logo are swapped in here — once, just before render.
// (react-pdf's StyleSheet.create returns plain objects, so this is safe.)
const COLOUR_PROPS = [
  'color', 'backgroundColor', 'borderColor',
  'borderTopColor', 'borderBottomColor', 'borderLeftColor', 'borderRightColor',
];

const applyBrandToPdf = () => {
  const b = brand();
  const colours = b.colors || {};
  const primary = colours.primary;
  const primaryDark = colours.primary_dark || primary;
  brandLogoPath = logoMark();
  if (!primary || primary.toLowerCase() === PURPLE.toLowerCase()) return;
  Object.keys(styles).forEach((key) => {
    const rule = styles[key];
    if (!rule || typeof rule !== 'object') return;
    COLOUR_PROPS.forEach((prop) => {
      if (rule[prop] === PURPLE) rule[prop] = primary;
      else if (rule[prop] === PURPLE_DEEP) rule[prop] = primaryDark;
    });
  });
};

export const generateCarePlanPDF = async (
  patient,
  sectionContents,
  medicalContactData,
  visionHearingData,
  crisisCycleData,
) => {
  applyBrandToPdf();
  const element = (
    <CarePlanPDF
      patient={patient}
      sectionContents={sectionContents}
      medicalContactData={medicalContactData}
      visionHearingData={visionHearingData}
      crisisCycleData={crisisCycleData}
    />
  );
  return pdf(element).toBlob();
};

export default CarePlanPDF;
